import { createReducer } from '@reduxjs/toolkit'

import actionTypes from './actionTypes'
// import mockVessels from './mockData' // Commented out to use real API data

/**
 * Filter and paginate vessels on the frontend
 * @param {Array} allVessels - All vessels from backend
 * @param {number} page - Current page number
 * @param {number} pageSize - Number of items per page
 * @param {string} searchQuery - Search query string
 * @returns {Object} - { vessels: filtered/paginated vessels, total: total count }
 */
const filterAndPaginateVessels = (allVessels, page, pageSize, searchQuery) => {
  // Filter vessels based on search query
  let filteredVessels = allVessels

  if (searchQuery && searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim()
    filteredVessels = allVessels.filter(vessel => {
      const vesselName = (vessel.vesselName || '').toLowerCase()
      const imoNumber = (vessel.imoNumber || '').toString().toLowerCase()
      const abbreviation = (
        vessel.vesselAbbreviation ||
        vessel.abbreviation ||
        ''
      ).toLowerCase()

      return (
        vesselName.includes(query) ||
        imoNumber.includes(query) ||
        abbreviation.includes(query)
      )
    })
  }

  const total = filteredVessels.length

  // Paginate the filtered results
  const startIndex = (page - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedVessels = filteredVessels.slice(startIndex, endIndex)

  return {
    total,
    vessels: paginatedVessels,
  }
}

const initialState = {
  allVessels: [], // Store all vessels from backend
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  requestingVessel: false,
  requestingVessels: false,
  saveVesselDetailsError: null,
  savingVesselDetails: false,
  searchQuery: '',
  vessel: null,
  vessels: [], // Filtered and paginated vessels for display
}

const vesselReducer = createReducer(initialState, builder => {
  builder
    // Request vessels
    .addCase(actionTypes.SET_REQUESTING_VESSELS, (state, action) => {
      state.requestingVessels = action.payload
    })
    .addCase(actionTypes.VESSELS_REQUEST_SUCCESSFUL, (state, action) => {
      // Store all vessels from backend (already unwrapped from envelope by saga)
      const allVessels = action.payload.vessels || action.payload

      state.allVessels = allVessels

      // Get pagination and search from payload (passed from saga)
      const page = action.payload.page || state.pagination.page
      const pageSize = action.payload.pageSize || state.pagination.pageSize
      const searchQuery =
        action.payload.searchQuery !== undefined
          ? action.payload.searchQuery
          : state.searchQuery

      // Filter and paginate on frontend
      const { vessels, total } = filterAndPaginateVessels(
        allVessels,
        page,
        pageSize,
        searchQuery
      )

      state.vessels = vessels
      state.pagination = {
        page,
        pageSize,
        total,
      }
    })
    .addCase(actionTypes.VESSELS_REQUEST_UNSUCCESSFUL, state => {
      state.vessels = []
      state.allVessels = []
    })

    // Request single vessel
    .addCase(actionTypes.SET_REQUESTING_VESSEL, (state, action) => {
      state.requestingVessel = action.payload
    })
    .addCase(actionTypes.VESSEL_REQUEST_SUCCESSFUL, (state, action) => {
      state.vessel = action.payload
    })
    .addCase(actionTypes.VESSEL_REQUEST_UNSUCCESSFUL, state => {
      state.vessel = null
    })

    // Pagination and search
    .addCase(actionTypes.SET_VESSEL_PAGE, (state, action) => {
      state.pagination.page = action.payload

      // Re-filter and paginate with new page
      const { vessels, total } = filterAndPaginateVessels(
        state.allVessels,
        action.payload,
        state.pagination.pageSize,
        state.searchQuery
      )
      state.vessels = vessels
      state.pagination.total = total
    })
    .addCase(actionTypes.SET_VESSEL_PAGE_SIZE, (state, action) => {
      state.pagination.pageSize = action.payload

      // Re-filter and paginate with new page size
      const { vessels, total } = filterAndPaginateVessels(
        state.allVessels,
        state.pagination.page,
        action.payload,
        state.searchQuery
      )
      state.vessels = vessels
      state.pagination.total = total
    })
    .addCase(actionTypes.SET_VESSEL_SEARCH_QUERY, (state, action) => {
      state.searchQuery = action.payload

      // Always reset to page 1 when search query changes
      state.pagination.page = 1

      // Re-filter and paginate with new search query
      const { vessels, total } = filterAndPaginateVessels(
        state.allVessels,
        1,
        state.pagination.pageSize,
        action.payload
      )
      state.vessels = vessels
      state.pagination.total = total
    })

    // Unified save vessel details
    .addCase(
      actionTypes.SET_REQUESTING_SAVE_VESSEL_DETAILS,
      (state, action) => {
        state.savingVesselDetails = action.payload
      }
    )
    .addCase(actionTypes.SET_SAVE_VESSEL_DETAILS_ERROR, (state, action) => {
      state.saveVesselDetailsError = action.payload
    })

    .addDefaultCase(state => state)
})

export default vesselReducer
